// Thin client for the /api/agenda and /api/andes endpoints. Errors (non-2xx,
// 401) are already surfaced by the global fetch interceptor in _app.js; callers
// await and handle the happy path, catching to stop their own flow.

async function jsonFetch(url, options) {
	const res = await fetch(url, options);
	if (!res.ok) {
		const err = new Error(`Request failed: ${res.status}`);
		err.status = res.status;
		throw err;
	}
	return res.status === 204 ? null : res.json();
}

/** Services (specialties) for the agenda filter. */
export function getServices() {
	return jsonFetch("/api/agenda/services");
}

/** The day board: agendas → slots + turns. `date` is YYYY-MM-DD. */
export function getDay(date, serviceId) {
	const params = new URLSearchParams({ date });
	if (serviceId) params.set("serviceId", serviceId);
	return jsonFetch(`/api/agenda/day?${params.toString()}`);
}

/** Admin turns list with per-field data provenance. `date` is YYYY-MM-DD. */
export function getTurnsList(date) {
	const params = new URLSearchParams({ date });
	return jsonFetch(`/api/turns?${params.toString()}`);
}

/** Patients waiting to be called (staff Sala de espera). */
export function getPresent(date, scheduleId) {
	const params = new URLSearchParams({ date });
	if (scheduleId) params.set("scheduleId", scheduleId);
	return jsonFetch(`/api/turns/present?${params.toString()}`);
}

/** Staff attention console: per consultorio, the turn in attention + the queue. */
export function getAttention(date) {
	return jsonFetch(`/api/turns/attention?${new URLSearchParams({ date })}`);
}

/** Calls a specific waiting patient. */
export function callTurn(id) {
	return jsonFetch(`/api/turns/${id}/call`, { method: "POST" });
}

/** Finalizes a turn (Atendido); frees the consultorio. */
export function finalizeTurn(id) {
	return jsonFetch(`/api/turns/${id}/finalize`, { method: "POST" });
}

/** Calls the next waiting patient of a consultorio. */
export function callNext(scheduleId, date) {
	return jsonFetch("/api/turns/call-next", {
		method: "POST",
		headers: { "Content-Type": "application/json" },
		body: JSON.stringify({ scheduleId, date }),
	});
}

/**
 * Waiting-room display board (codes only). Pass `all` to show every consultorio
 * with activity; otherwise the board is limited to `scheduleIds`.
 */
export function getDisplay(date, scheduleIds, all = false) {
	const params = new URLSearchParams({ date });
	if (all) params.set("all", "true");
	else params.set("scheduleIds", scheduleIds.join(","));
	return jsonFetch(`/api/turns/display?${params.toString()}`);
}

/** Patient search against ANDES (fuzzy over name + DNI, single free-text query). */
export function searchPatients(query) {
	const params = new URLSearchParams({ q: query });
	return jsonFetch(`/api/agenda/patients?${params.toString()}`);
}

/** Turnos available to a patient (patient-first reception flow). */
export function getPatientAvailability(patientId, { conceptId, teleConsulta } = {}) {
	const params = new URLSearchParams({ patientId });
	if (conceptId) params.set("conceptId", conceptId);
	if (teleConsulta !== undefined) params.set("teleConsulta", String(teleConsulta));
	return jsonFetch(`/api/agenda/availability?${params.toString()}`);
}

/** Books a turno for a patient (REST reserveTurn). Phase 2 — backend stub. */
export function reserveTurn(payload) {
	return jsonFetch("/api/agenda/reserve", {
		method: "POST",
		headers: { "Content-Type": "application/json" },
		body: JSON.stringify(payload),
	});
}

/** Books a turn. Returns { code, synced, warning? }. */
export function createTurn({ slotId, scheduleId, patientId, date }) {
	return jsonFetch("/api/turns", {
		method: "POST",
		headers: { "Content-Type": "application/json" },
		body: JSON.stringify({ slotId, scheduleId, patientId, date }),
	});
}

/** Cancels a turn we booked (admin). Returns { outcome, synced }. */
export function cancelTurn(id) {
	return jsonFetch(`/api/turns/${id}/cancel`, { method: "POST" });
}

/** Cancels an ANDES-only appointment (no local turn). `date` locates its slot. */
export function cancelAppointment(appointmentId, date) {
	return jsonFetch(`/api/turns/appointment/${appointmentId}/cancel`, {
		method: "POST",
		headers: { "Content-Type": "application/json" },
		body: JSON.stringify({ date }),
	});
}

/** ANDES availability for the sidebar indicator. */
export function getAndesStatus() {
	return jsonFetch("/api/andes/status");
}
